import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Building2, GraduationCap, ArrowRight, Search, AlertCircle, ShieldCheck, CalendarClock } from 'lucide-react';
import { PageHeader } from '../components/ui/PageHeader';
import { Card, CardContent } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import { ContactCTA } from '../components/ContactCTA';
import { OffreForm } from '../components/OffreForm';
import { StructuredData } from '../components/StructuredData';
import { useSeo } from '../hooks/useSeo';
import { OFFRES } from '../data/offres';
import { CFA_INFO, SITE_URL, RENTREE } from '../data/constants';
export function Offres() {
  useSeo({
    title: "Nos offres d'alternance dans l'Yonne — Joigny, Sens",
    description: "Consultez les offres d'alternance en vente et relation client proposées par les entreprises partenaires de Zillenium Alternance. Prêt-à-porter, grande distribution, automobile. Candidature gratuite.",
    path: '/offres'
  });
  const ouvertes = OFFRES.filter(o => o.ouverte);
  const [filtre, setFiltre] = useState<string>('toutes');
  const formations = Array.from(new Set(ouvertes.flatMap(o => o.formations.map(f => f.label))));
  const affichees = filtre === 'toutes' ? ouvertes : ouvertes.filter(o => o.formations.some(f => f.label === filtre));
  const jobPostings = ouvertes.map(o => ({
    '@context': 'https://schema.org',
    '@type': 'JobPosting',
    title: o.poste,
    description: `${o.secteur}. Missions : ${o.missions.join(', ')}.`,
    employmentType: 'APPRENTICESHIP',
    hiringOrganization: {
      '@type': 'Organization',
      name: `Entreprise partenaire de ${CFA_INFO.name}`,
      url: SITE_URL
    },
    jobLocation: {
      '@type': 'Place',
      address: {
        '@type': 'PostalAddress',
        addressLocality: o.ville ?? 'Yonne',
        addressRegion: 'Bourgogne-Franche-Comté',
        addressCountry: 'FR'
      }
    },
    educationRequirements: o.formations.map(f => f.label).join(' ou ')
  }));
  return <div className="flex flex-col w-full" data-id="element-2314" data-mp="1qnofy7hzecd2">
      <StructuredData id="job-postings" data={jobPostings} data-id="element-2315" data-mp="1vovpaazrvjlu" />

      <PageHeader title="Nos offres d'alternance" subtitle="Les postes actuellement proposés par les entreprises partenaires de Zillenium Alternance. Candidature gratuite, accompagnement assuré par notre équipe." data-id="element-2316" data-mp="1vmhu8253e7ea" />

      {/* Accroche : disponibilité et rentrée */}
      <div className="bg-primary-dark text-white" data-id="element-2317" data-mp="17zjjll3nltoa">
        <div className="container max-w-5xl mx-auto py-8" data-id="element-2318" data-mp="1c03e0gwuej6q">
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-5" data-id="element-2319" data-mp="1byihwmuvqs6r">
            <span className="w-12 h-12 rounded-xl bg-brand-orange flex items-center justify-center shrink-0" data-id="element-2320" data-mp="1gsmq19fvt69f">
              <CalendarClock className="w-6 h-6 text-white" data-id="element-2321" data-mp="k7cosqdktuqz" />
            </span>
            <p className="text-lg md:text-xl font-bold leading-snug" data-id="element-2322" data-mp="o9hi1o044pd3">
              Offres d'alternance disponibles — démarrage immédiat possible en
              entreprise —{' '}
              <span className="text-brand-orange" data-id="element-2323" data-mp="bthpuzof3gyx">
                rentrée en formation le {RENTREE.dateLabel}
              </span>
              .
            </p>
          </div>
        </div>
      </div>

      <div className="py-16 md:py-24 bg-muted relative overflow-hidden" data-id="element-2324" data-mp="2qyq4ofxhkgw">
        <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-primary rounded-full blur-[120px] opacity-10 pointer-events-none" data-id="element-2325" data-mp="3tykygudleb43" />
        <div className="container max-w-6xl mx-auto relative z-10" data-id="element-2326" data-mp="3p5rzkn4t1wzx">
          {/* Note de confidentialité */}
          <Card className="border-none shadow-md bg-white mb-10" data-id="element-2327" data-mp="3k8i2clb6ch1j">
            <CardContent className="p-6 flex items-start gap-4" data-id="element-2328" data-mp="3g7oajgum2i3d">
              <span className="w-11 h-11 rounded-xl bg-lavande flex items-center justify-center shrink-0" data-id="element-2329" data-mp="3b7abupewgki0">
                <ShieldCheck className="w-5 h-5 text-primary" data-id="element-2330" data-mp="367xgfg9yq53t" />
              </span>
              <p className="text-muted-foreground leading-relaxed" data-id="element-2331" data-mp="1a5cb7tjjczdm">
                Le nom des entreprises n'est pas communiqué publiquement. Il
                vous est précisé lors de l'échange avec notre équipe, une fois
                votre candidature étudiée.
              </p>
            </CardContent>
          </Card>

          {/* Compteur + filtres */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-6 mb-10" data-id="element-2332" data-mp="15cri6u8geqb5">
            <p className="text-lg font-bold text-primary-dark" data-id="element-2333" data-mp="rdjdjltiupzk">
              {ouvertes.length} offre{ouvertes.length > 1 ? 's' : ''} ouverte
              {ouvertes.length > 1 ? 's' : ''} aux candidatures
            </p>
            {formations.length > 1 && <div className="flex flex-wrap gap-2" data-id="element-2334" data-mp="meygfv9kgmfc">
                <button type="button" onClick={() => setFiltre('toutes')} className={`text-sm font-semibold rounded-full px-4 py-2 transition-colors ${filtre === 'toutes' ? 'bg-primary text-white' : 'bg-white text-primary-dark hover:bg-lavande'}`} data-id="element-2335" data-mp="mhkw6i6m288n">
                  Toutes
                </button>
                {formations.map(f => <button key={f} type="button" onClick={() => setFiltre(f)} className={`text-sm font-semibold rounded-full px-4 py-2 transition-colors ${filtre === f ? 'bg-primary text-white' : 'bg-white text-primary-dark hover:bg-lavande'}`} data-id="element-2336" data-mp="ey1vmy1bpwcb">
                    {f}
                  </button>)}
              </div>}
          </div>

          {affichees.length === 0 ? <Card className="border-none shadow-lg bg-white" data-id="element-2337" data-mp="2zok89mj0cxb0">
              <CardContent className="p-12 text-center" data-id="element-2338" data-mp="2uoekihfuzuy7">
                <div className="w-16 h-16 bg-lavande rounded-2xl flex items-center justify-center mx-auto mb-6" data-id="element-2339" data-mp="3kbihkpenjmk8">
                  <Search className="w-8 h-8 text-primary" data-id="element-2340" data-mp="3ockev0p0dojk" />
                </div>
                <h2 className="text-2xl font-extrabold font-heading text-primary-dark mb-4" data-id="element-2341" data-mp="3tc6u1foedev0">
                  Aucune offre pour cette formation
                </h2>
                <p className="text-muted-foreground leading-relaxed max-w-xl mx-auto" data-id="element-2342" data-mp="2771txrowc1n">
                  De nouvelles offres arrivent régulièrement. Déposez une
                  candidature spontanée ci-dessous : nous vous contacterons dès
                  qu'un poste correspond à votre profil.
                </p>
              </CardContent>
            </Card> : <div className="grid md:grid-cols-2 gap-6" data-id="element-2343" data-mp="kv2a524j8xnd">
              {affichees.map(offre => <Card key={offre.id} className="border-none shadow-lg bg-white overflow-hidden hover:-translate-y-1 hover:shadow-xl transition-all duration-300 flex flex-col" data-id="element-2344" data-mp="pwrkefjxl7ae">
                  <div className="h-2 w-full bg-primary shrink-0" data-id="element-2345" data-mp="2faiuwww9px3j" />
                  <CardContent className="p-7 flex flex-col flex-1" data-id="element-2346" data-mp="2acpqo9eizd5w">
                    {/* Formations visées */}
                    <div className="flex flex-wrap gap-2 mb-4" data-id="element-2347" data-mp="25eo7zpl0hfuy">
                      {offre.formations.map(f => <Link key={f.id} to={`/formations/${f.id}`} data-id="element-2348" data-mp="20hpdh5xfr6w8">
                          <Badge className="bg-lavande text-primary-dark hover:bg-lavande/70 border-none font-bold cursor-pointer" data-id="element-2349" data-mp="2y2k2qor0sxp3">
                            {f.label}
                          </Badge>
                        </Link>)}
                    </div>

                    <h2 className="text-xl md:text-2xl font-extrabold font-heading text-primary-dark leading-tight mb-4" data-id="element-2350" data-mp="2jd5nx14odo2b">
                      {offre.poste}
                    </h2>

                    {/* Repères */}
                    <div className="space-y-2.5 mb-6" data-id="element-2351" data-mp="3c1jptryvx4j5">
                      <p className="flex items-start gap-2.5 text-sm text-muted-foreground" data-id="element-2352" data-mp="2kerrcsggxoy1">
                        <Building2 className="w-4 h-4 text-brand-orange shrink-0 mt-0.5" data-id="element-2353" data-mp="2p6jlfcyu2csx" />
                        <span data-id="element-2354" data-mp="2bevb9f859yel">{offre.secteur}</span>
                      </p>
                      {offre.ville && <p className="flex items-start gap-2.5 text-sm text-muted-foreground" data-id="element-2355" data-mp="2gcwtjxxqxtp0">
                          <MapPin className="w-4 h-4 text-brand-orange shrink-0 mt-0.5" data-id="element-2356" data-mp="3430utyngphcu" />
                          <span data-id="element-2357" data-mp="385e0yo64fha5">{offre.ville}</span>
                        </p>}
                      <p className="flex items-start gap-2.5 text-sm text-muted-foreground" data-id="element-2358" data-mp="2u5mgq5p0h4np">
                        <GraduationCap className="w-4 h-4 text-brand-orange shrink-0 mt-0.5" data-id="element-2359" data-mp="2z3o9jic8oicd" />
                        <span data-id="element-2360" data-mp="1iq7i6xh5m4qh">
                          Entreprise partenaire de {CFA_INFO.name}
                        </span>
                      </p>
                    </div>

                    {/* Missions */}
                    <h3 className="font-bold text-sm uppercase tracking-wider text-primary-dark mb-3" data-id="element-2361" data-mp="1g4qaur30b8jc">
                      Missions principales
                    </h3>
                    <ul className="space-y-2 mb-6 flex-1" data-id="element-2362" data-mp="1p67n1ydamq2r">
                      {offre.missions.map(m => <li key={m} className="flex gap-3 text-muted-foreground leading-relaxed" data-id="element-2363" data-mp="1k6lc5vkxtlx4">
                          <span className="mt-2 w-1.5 h-1.5 rounded-full bg-brand-orange shrink-0" data-id="element-2364" data-mp="wf63ll2ki8df" />
                          <span data-id="element-2365" data-mp="se46paw4i8em">{m}</span>
                        </li>)}
                    </ul>

                    {offre.condition && <p className="flex items-center gap-2.5 bg-brand-orange/10 text-primary-dark font-semibold text-sm rounded-xl px-4 py-3 mb-6" data-id="element-2366" data-mp="11f2bhvedyjy9">
                        <AlertCircle className="w-4 h-4 text-brand-orange shrink-0" data-id="element-2367" data-mp="8s7qmxdnpik9" />
                        {offre.condition}
                      </p>}

                    <Button className="w-full bg-brand-orange hover:bg-[#E67E22] text-white font-bold py-6 group mt-auto" asChild data-id="element-2368" data-mp="3joj8pfvc2iac">
                      <a href="#candidature-offre" data-id="element-2369" data-mp="39usk8az7i7un">
                        Postuler
                        <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" data-id="element-2370" data-mp="35quoma93bf6c" />
                      </a>
                    </Button>
                  </CardContent>
                </Card>)}
            </div>}

          {/* Formulaire de candidature */}
          <div id="candidature-offre" className="mt-20 max-w-3xl mx-auto scroll-mt-28" data-id="element-2371" data-mp="2r0594voljdwo">
            <OffreForm data-id="element-2372" data-mp="2i1jsj62oq8yj" />
          </div>

          {/* Note employeurs */}
          <Card className="border-none shadow-lg bg-primary-dark text-white mt-16 overflow-hidden" data-id="element-2373" data-mp="2i3mhrnw0jy27">
            <div className="h-2 w-full bg-brand-orange" data-id="element-2374" data-mp="2d4jmuw9qz87y" />
            <CardContent className="p-8 md:p-10 flex flex-col md:flex-row md:items-center gap-6" data-id="element-2375" data-mp="sjcweoqilh55">
              <div className="md:flex-1" data-id="element-2376" data-mp="xig28bebjre6">
                <h2 className="text-2xl font-extrabold font-heading mb-3" data-id="element-2377" data-mp="xllqjof4ip8u">
                  Vous êtes une entreprise ?
                </h2>
                <p className="text-lavande leading-relaxed" data-id="element-2378" data-mp="16kp82nyn6wl8">
                  Publiez gratuitement votre offre d'alternance et recevez des
                  profils présélectionnés par notre équipe.
                </p>
              </div>
              <Button size="lg" className="bg-white hover:bg-lavande text-primary-dark font-bold px-8 py-6 shrink-0" asChild data-id="element-2379" data-mp="dw1nkulpwb8q">
                <Link to="/entreprises" data-id="element-2380" data-mp="nmw7vtfel0rj">Déposer une offre</Link>
              </Button>
            </CardContent>
          </Card>

          <div className="mt-16" data-id="element-2381" data-mp="3m98s8xm4kl1r">
            <ContactCTA title="Une offre vous intéresse ? Appelez-nous" subtitle="Nous vous expliquons le poste et les étapes de recrutement en quelques minutes. Vous pouvez aussi nous écrire sur WhatsApp." variant="light" data-id="element-2382" data-mp="3r9wi3fymavue" />
          </div>
        </div>
      </div>
    </div>;
}